import React, { useState, useEffect } from 'react';
import { Form, Button, Modal, FormInstance } from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import Logo from './svg/Logo';
import EmailIcon from './svg/EmailIcon';
import PasswordIcon from './svg/PasswordIcon';
import HidePasswordEyeIcon from './svg/HidePasswordEyeIcon';
import ShowPasswordEyeIcon from './svg/ShowPasswordEyeIcon';
import ArrowRight from './svg/ArrowRight';
import CircledBackArrow from './svg/CircledBackArrow';
import UserIcon from './svg/UserIcon';
import CloseIcon from './svg/CloseIcon';

/** Custom Input to bypass library-specific autofill/activation styling issues */
interface AuthInputProps {
  id?: string;
  placeholder?: string;
  prefix?: React.ReactNode;
  type?: string;
  value?: any;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  className?: string;
  isPassword?: boolean;
  disabled?: boolean;
}

const AuthInput = ({ id, placeholder, prefix, type = 'text', value, onChange, className, isPassword, disabled }: AuthInputProps) => {
  const [visible, setVisible] = useState(false);

  // If it's a password field, toggle between 'password' and 'text'
  const inputType = isPassword ? (visible ? 'text' : 'password') : type;

  return (
    <div className={`flex items-center group bg-[#f1f5f9] border border-transparent rounded-[10px] px-4 h-[40px] md:h-[47px] w-full transition-all duration-200 hover:bg-[#e2e8f0] focus-within:bg-[#e2e8f0] ${disabled ? 'opacity-70 cursor-not-allowed bg-[#e2e8f0]' : ''} ${className || ''}`}>
      {prefix && <span className="mr-2 flex items-center shrink-0">{prefix}</span>}
      <input
        id={id}
        type={inputType}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        disabled={disabled}
        className="flex-1 bg-transparent border-none outline-none h-full w-full text-[14px] font-['Lexend',sans-serif] text-[#1f2a37] placeholder:text-[#1f2a3770] placeholder:font-light disabled:cursor-not-allowed [&:-webkit-autofill]:[box-shadow:0_0_0_1000px_#f1f5f9_inset] group-hover:[&:-webkit-autofill]:[box-shadow:0_0_0_1000px_#e2e8f0_inset] group-focus-within:[&:-webkit-autofill]:[box-shadow:0_0_0_1000px_#e2e8f0_inset] [&:-webkit-autofill]:[-webkit-text-fill-color:#1f2a37] [&:-webkit-autofill]:transition-[background-color_5000s_ease-in-out_0s]"
      />
      {isPassword && (
        <span className="cursor-pointer flex items-center justify-center opacity-60 ml-2 transition-opacity duration-200 shrink-0 hover:opacity-100" onClick={() => setVisible(!visible)}>
          {visible ? (
            <HidePasswordEyeIcon className="w-[15px] h-[12px] sm:w-[18px] sm:h-[18px]" />
          ) : (
            <ShowPasswordEyeIcon className="w-[15px] h-[12px] sm:w-[18px] sm:h-[18px]" />
          )}
        </span>
      )}
    </div>
  );
};

interface AuthComponentProps {
  titleText?: string;
  titleSpanText?: string;
  subtitleText?: string;
  isPassword?: boolean;
  bottonText?: string;
  linkText?: string;
  linkPath?: string;
  onSubmit: (value: any) => void;
  loading?: boolean;
  isUpdatePassword?: boolean;
  isSignup?: boolean;
  initialValues?: any;
  showBackArrow?: boolean;
  onBackClick?: () => void;
  noteText?: string;
  roleConfig?: any;
  hideSignupLink?: boolean;
  errorMsg?: string | null;
  onErrorClose?: () => void;
  form?: FormInstance;
  isEmailDisabled?: boolean;
  modalButtonText?: string;
  onModalButtonClick?: () => void;
  modalTitle?: string;
  modalType?: 'success' | 'error';
  modalMsg?: string | null;
  onModalClose?: () => void;
}

function AuthComponent({
  titleText = 'Welcome',
  titleSpanText = 'Back',
  subtitleText = 'Create password for your account',
  isPassword = true,
  bottonText = 'Login',
  linkText = 'Forgot Password?',
  linkPath = '/forgotpassword',
  onSubmit,
  loading = false,
  isUpdatePassword = false,
  isSignup = false,
  initialValues,
  showBackArrow = false,
  onBackClick,
  noteText,
  roleConfig,
  hideSignupLink = false,
  errorMsg = null,
  onErrorClose,
  form,
  isEmailDisabled = false,
  modalButtonText,
  onModalButtonClick,
  modalTitle,
  modalType,
  modalMsg,
  onModalClose,
}: AuthComponentProps) {
  const [internalForm] = Form.useForm();
  const activeForm = form || internalForm;
  const navigate = useNavigate();

  const activeModalMsg = modalMsg !== undefined ? modalMsg : errorMsg;
  const isModalOpen = !!activeModalMsg;
  const activeModalType = modalType || (errorMsg ? 'error' : 'success');

  const defaultButtonText = modalButtonText || (
    activeModalType === 'success' || isUpdatePassword ? 'Login' : 'Retry'
  );

  const handleModalClose = () => {
    if (onModalClose) {
      onModalClose();
    } else if (onErrorClose) {
      onErrorClose();
    }
  };

  const handleModalButtonClick = () => {
    if (onModalButtonClick) {
      onModalButtonClick();
    } else if (onModalClose) {
      onModalClose();
    } else if (isUpdatePassword || activeModalType === 'success') {
      if (onErrorClose) onErrorClose();
      navigate('/login');
    } else {
      if (onErrorClose) onErrorClose();
    }
  };

  useEffect(() => {
    if (initialValues) {
      activeForm.setFieldsValue(initialValues);
    }
  }, [initialValues, activeForm]);

  const renderErrorMessage = (msg: string | null) => {
    if (!msg) return 'Invalid email or password';

    const match = msg.match(/(.*?\b)(create an account)(\b.*)/i);
    if (match) {
      const prefix = match[1];
      const linkText = match[2];
      const suffix = match[3];
      return (
        <>
          {prefix}
          <Link
            to="/signup"
            onClick={() => {
              if (onErrorClose) onErrorClose();
            }}
            className="font-medium text-[#4682b4] underline hover:text-[#3b6d98] cursor-pointer"
          >
            {linkText}
          </Link>
          {suffix}
        </>
      );
    }

    return msg;
  };

  return (
    <>
      <div className="w-full flex flex-col items-center justify-center px-[52px] py-2 lg:py-[10px] lg:px-[24px] font-['DM_Sans',sans-serif]">
        <div className="w-full max-w-full sm:max-w-[400px] md:max-w-[420px] xl:max-w-[440px] flex flex-col items-center">
          {/* Brand Logo */}
          <div className="text-center mb-[20px] lg:mb-[25px]">
            <Logo className="w-[185px] lg:w-[205px] h-auto object-contain" />
          </div>

          {/* Login Card */}
          <div className="premium-card dark-blue-tint relative z-10 w-full p-[25px] sm:p-[30px]">

            <div className="text-center mb-[20px] md:mb-[30px]">
              <h1 className="font-['Lexend',sans-serif] flex items-center justify-center text-[16px] lg:text-[24px] text-[#1f2a37] m-0 mb-[10px] md:mb-[14px]">
                {showBackArrow && (
                  <span className="cursor-pointer mr-[23px] inline-flex items-center transition-transform duration-200 hover:-translate-x-[3px]" onClick={onBackClick}>
                    <CircledBackArrow className="w-[18px] h-[18px] md:w-[28px] md:h-[28px]" />
                  </span>
                )}
                <span className="font-bold">{titleText}</span>
                <span className="font-normal pl-1.5">{titleSpanText}</span>
              </h1>
              {subtitleText && (
                <p className="font-['Lexend',sans-serif] font-light text-[10px] lg:text-[15px] text-[#555d67] m-0">
                  {subtitleText}
                </p>
              )}
            </div>

            <div className="w-full">
              <Form form={activeForm} layout="vertical" onFinish={onSubmit} initialValues={{ requestedRole: 'User', ...initialValues }} className="[&_.ant-form-item-label]:hidden [&_.ant-form-item]:mb-0 [&_.ant-form-item]:pb-0 [&_.ant-form-item-control-input-content]:mb-0 [&_.ant-form-item-control-input-content]:pb-0 [&_.ant-form-item-explain-error]:text-[11px] [&_.ant-form-item-explain-error]:text-[#d4183d] [&_.ant-form-item-explain-error]:mt-1 [&_.ant-form-item-explain-error]:pl-4 space-y-[24px] sm:space-y-[30px]">
                {/* Hidden token field for password reset */}
                <Form.Item name="token" style={{ display: 'none' }}>
                  <input type="hidden" />
                </Form.Item>

                {/* Full Name Field (Signup Only) */}
                {isSignup && (
                  <div className="mb-0">
                    <label htmlFor="auth-name" className="block font-['Lexend',sans-serif] text-[12px] lg:text-[16px] font-medium text-[#1f2a37] mb-[11px] sm:mb-[16px]">Full Name</label>
                    <Form.Item
                      name="name"
                      rules={[{ required: true, message: 'Please enter your full name' }]}
                      style={{ marginBottom: '0' }}
                    >
                      <AuthInput
                        id="auth-name"
                        placeholder="Enter your full name"
                        prefix={<UserIcon className="w-[16px] h-[18px] auth-input-icon-svg auth-icon-user" />}
                      />
                    </Form.Item>
                  </div>
                )}

                {/* Email Field */}
                <div className="mb-0">
                  <label htmlFor="auth-email" className="block font-['Lexend',sans-serif] text-[12px] lg:text-[16px] font-medium text-[#1f2a37] mb-[11px] sm:mb-[16px]">Email Address</label>
                  <Form.Item
                    name="email"
                    rules={[
                      { required: true, message: 'Please input your email!' },
                      { type: 'email', message: 'Please enter a valid email address!' }
                    ]}
                    style={{ marginBottom: '0' }}
                  >
                    <AuthInput
                      id="auth-email"
                      placeholder="Enter your email"
                      prefix={<EmailIcon className="w-[15px] h-[12px] sm:w-[21px] sm:h-[18px] auth-input-icon-svg auth-icon-email" />}
                      type="email"
                      disabled={isEmailDisabled}
                    />
                  </Form.Item>
                </div>

                {/* Hidden requestedRole field for signup (default: User) */}
                {isSignup && (
                  <Form.Item name="requestedRole" initialValue="User" style={{ display: 'none' }}>
                    <input type="hidden" />
                  </Form.Item>
                )}

                {/* Password Fields */}
                {isPassword && (
                  <>
                    <div className="mb-0">
                      <label htmlFor="auth-password" className="block font-['Lexend',sans-serif] text-[12px] lg:text-[16px] font-medium text-[#1f2a37] mb-[11px] sm:mb-[16px]">Password</label>
                      <Form.Item
                        name="password"
                        rules={[{ required: true, message: 'Please input your password!' }]}
                        style={{ marginBottom: '0' }}
                      >
                        <AuthInput
                          id="auth-password"
                          placeholder="Enter your password"
                          prefix={<PasswordIcon className="w-[12px] h-[15px] sm:w-[17px] sm:h-[22px] auth-input-icon-svg auth-icon-password" />}
                          isPassword={true}
                        />
                      </Form.Item>
                    </div>

                    {isUpdatePassword && (
                      <div className="mb-0">
                        <label htmlFor="auth-cpassword" className="block font-['Lexend',sans-serif] text-[12px] lg:text-[16px] font-medium text-[#1f2a37] mb-[11px] sm:mb-[16px]">Confirm Password</label>
                        <Form.Item
                          name="cpassword"
                          rules={[
                            { required: true, message: 'Please confirm your password!' },
                            ({ getFieldValue }) => ({
                              validator(_, value) {
                                if (!value || getFieldValue('password') === value) {
                                  return Promise.resolve();
                                }
                                return Promise.reject(new Error('Passwords do not match!'));
                              },
                            }),
                          ]}
                          style={{ marginBottom: '0' }}
                        >
                          <AuthInput
                            id="auth-cpassword"
                            placeholder="Confirm your password"
                            prefix={<PasswordIcon className="w-[12px] h-[15px] sm:w-[17px] sm:h-[22px] auth-input-icon-svg" />}
                            isPassword={true}
                          />
                        </Form.Item>
                      </div>
                    )}
                  </>
                )}

                {/* Submit Button */}
                <Form.Item className="!mt-[31px] sm:!mt-[43px]" style={{ marginBottom: 0 }}>
                  <div className="flex justify-center mb-0 p-0">
                    <Button
                      htmlType="submit"
                      loading={loading}
                      className="inline-flex items-center justify-center gap-2.5 sm:gap-2.5 lg:gap-[15px] h-auto min-h-[36px] sm:min-h-[38px] md:min-h-[44px] lg:min-h-[44px] px-1.5 py-1.5 sm:px-1.5 md:px-2 md:py-2 rounded-[20px] bg-[#4682b4] hover:!bg-[#4682b4] hover:!text-white hover:-translate-y-0.5 active:translate-y-0 border-none text-[12px] sm:text-[13px] lg:text-[16px] font-normal font-['Lexend',sans-serif] text-white leading-none cursor-pointer transition-all duration-300 shadow-[0_4px_12px_rgba(70,130,180,0.3)]"
                    >
                      <div className="flex items-center justify-center w-[22px] h-[22px] md:w-[29px] md:h-[29px] bg-white border-[1.5px] border-[#4682b4] rounded-full shrink-0 [&>svg]:w-[10px] [&>svg]:h-[10px] md:[&>svg]:w-auto md:[&>svg]:h-auto">
                        <ArrowRight />
                      </div>
                      <span className="pr-[15px] md:pr-[27px]">
                        {loading ? "Please wait..." : bottonText}
                      </span>
                    </Button>
                  </div>
                </Form.Item>

                {/* Dynamic Bottom Link */}
                {!isSignup && (
                  <div className="text-center !mt-[13px] sm:!mt-[23px]">
                    <Link to={linkPath} className="text-[#1f2a37] font-['Lexend',sans-serif] text-[12px] sm:text-[14px] font-semibold no-underline transition-all duration-200 hover:text-[#4682b4] hover:underline hover:underline-offset-4">
                      {linkText}
                    </Link>
                  </div>
                )}
              </Form>
            </div>
          </div>

          {/* Sign Up Link */}
          {!hideSignupLink && (
            <div className="text-center mt-[31px] sm:mt-[33px]">
              <p className="font-['Lexend',sans-serif] font-light text-[11px] sm:text-[12px] lg:text-[14px] text-[#808080] m-0">
                Need access to the system?{' '}
                <Link to="/signup" className="font-['Lexend',sans-serif] font-semibold text-[#1f2a37] !no-underline transition-colors duration-200 hover:text-[#4682b4]">
                  Start Trial
                </Link>
              </p>
            </div>
          )}
        </div>

        {noteText && (
          <div className="text-center mt-8 max-w-[500px] self-center">
            <p className="font-['Lexend',sans-serif] font-light text-[10px] lg:text-[14px] leading-[18px] lg:leading-normal text-center text-[#555d67] m-0">
              {noteText.startsWith('Note:') ? (
                <>
                  <span className="font-medium">Note:</span> {noteText.replace('Note:', '').trim()}
                </>
              ) : (
                noteText
              )}
            </p>
          </div>
        )}
      </div>

      <Modal
        open={isModalOpen}
        onCancel={handleModalClose}
        footer={null}
        closeIcon={null}
        centered
        className="[&_.ant-modal-content]:!rounded-[24px] sm:[&_.ant-modal-content]:!rounded-[16px] md:[&_.ant-modal-content]:!rounded-[24px] [&_.ant-modal-content]:!p-0 [&_.ant-modal-content]:!overflow-hidden !w-[85%] sm:!w-[320px] md:!w-[400px]"
      >
        <div className="p-[30px_15px_20px] sm:p-[30px_20px_25px] md:p-[50px_40px_40px] text-center relative">
          <button className="absolute top-5 right-5 bg-transparent border-none text-[#6b7280] w-[25px] h-[25px] flex items-center justify-center p-0 cursor-pointer transition-colors duration-200 hover:text-[#4682b4]" onClick={handleModalClose}>
            <CloseIcon />
          </button>
          <h2 className="font-['Lexend',sans-serif] text-[18px] md:text-[24px] font-normal text-[#1f2a37] mb-2 md:mb-3">
            {modalTitle ? (
              <span className="font-semibold">{modalTitle}</span>
            ) : (
              <>
                <span className="font-semibold">{isSignup ? 'Signup' : isUpdatePassword ? 'Password' : 'Login'}</span> {activeModalType === 'success' ? 'Success' : 'Error'}
              </>
            )}
          </h2>
          <p className="font-['Lexend',sans-serif] text-[11px] sm:text-[12px] md:text-[14px] font-light text-[#6b7280] mb-[15px] sm:mb-[20px] md:mb-[32px]">
            {renderErrorMessage(activeModalMsg)}
          </p>
          <div className="flex justify-center">
            <Button
              className="inline-flex items-center justify-center gap-2.5 sm:gap-2.5 lg:gap-[15px] h-auto min-h-[36px] sm:min-h-[38px] md:min-h-[44px] lg:min-h-[44px] px-1.5 py-1.5 sm:px-1.5 md:px-2 rounded-[20px] bg-[#4682b4] hover:bg-[#3b6d98] hover:-translate-y-0.5 active:translate-y-0 border-none text-[12px] sm:text-[13px] lg:text-[16px] font-normal font-['Lexend',sans-serif] text-white leading-none cursor-pointer transition-all duration-300 shadow-[0_4px_12px_rgba(70,130,180,0.3)] hover:shadow-[0_6px_16px_rgba(70,130,180,0.4)] !min-w-[110px] sm:!min-w-[120px] md:!min-w-[140px]"
              onClick={handleModalButtonClick}
            >
              <div className="flex items-center justify-center w-[22px] h-[22px] md:w-[29px] md:h-[29px] bg-white border-[1.5px] border-[#4682b4] rounded-full shrink-0 [&>svg]:w-[10px] [&>svg]:h-[10px] md:[&>svg]:w-auto md:[&>svg]:h-auto">
                <ArrowRight />
              </div>
              <span className="pr-[15px] md:pr-[27px]">{defaultButtonText}</span>
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}

export default AuthComponent;